import { createSlice, isAnyOf } from "@reduxjs/toolkit";
import {
	updateCartItem, addDiscountCode, fetchCart, removeCartItem, removeCartItems, beginCheckout, addCartItem, addCartItems
} from "actions/cart";

const initialState = {
	items: {},
	delivery: {
		intervalCount: 0,
		interval: "",
	},
	checkoutUrl: "",
	totalPrice: 0,
	totalQuantity: 0,
	totalProgress: 0,
	discountCodes: [],
	isOpen: false,
	loading: true,
	allGiftCards: false,
	removeCount: 0,
	checkoutLoading: false,
	activeBox: null,
	minBox: null,
	bundlesAdded: false,
	mealsPerWeek: 0,
};

const cartSlice = createSlice({
	name: "cart",
	initialState,
	reducers: {
		openCart: (state) => {
			state.isOpen = true;
		},
		closeCart: (state) => {
			state.isOpen = false;
		},
		setDeliveryFrequency: (state, action) => {
			state.delivery = action.payload;
		},
		setMealsPerWeek: (state, action) => {
			state.mealsPerWeek = action.payload;
		},
		toggleCheckoutLoading: (state) => {
			state.checkoutLoading = !state.checkoutLoading;
		},
		resetRemoveCount: (state) => {
			state.removeCount = 0;
		},
		selectBox: (state, action) => {
			state.activeBox = action.payload;
			state.bundlesAdded = false;
		},
		selectMinBox: (state, action) => {
			state.minBox = action.payload;
			state.bundlesAdded = false;
		},
		addCartItems: (state) => {
			state.bundlesAdded = true;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(addCartItem.pending, (state, action) => {
				const product = action.meta.arg;
				const { title, image, type, id, variant = {} } = product || {};
				const totalProgress = state.totalProgress + (variant.weight >= 1 ? variant.weight : 0);

				if (!id || !title || !image || !variant.id) return;

				if (!state.items[variant.id]) {
					state.items[variant.id] = {
						title,
						image: { src: image, altText: title },
						quantity: 1,
						id,
						type,
						variant
					};
				}

				state.totalQuantity += 1;
				state.updating = true;
				state.totalProgress = variant.weight > 0 ? totalProgress : state.totalProgress;
				state.removeCount = 0;
			})
			.addCase(updateCartItem.pending, (state, action) => {
				const { variantId, quantity, totalQuantity, totalProgress } = action.meta.arg;

				if (!variantId || !quantity || !totalQuantity || !totalProgress) return;

				const existingQuantity = state.items[variantId]?.quantity;
				const isIncreasing = quantity > existingQuantity;

				state.items[variantId].quantity = quantity;
				state.totalQuantity = totalQuantity;
				state.totalProgress = totalProgress;
				state.removeCount = !isIncreasing ? state.removeCount + 1 : 0;
				state.updating = true;
			})
			.addCase(removeCartItem.pending, (state, action) => {
				const { variantId, weight } = action.meta.arg || {};
				if (!variantId || !weight) return;
				delete state.items[variantId];
				state.totalQuantity -= 1;
				state.totalProgress -= weight;
				state.removeCount += 1;
				state.updating = true;
			})
			.addCase(beginCheckout.pending, (state) => {
				state.checkoutLoading = true;
			}).addCase(beginCheckout.fulfilled, (state) => {
				state.checkoutLoading = false;
			}).addCase(beginCheckout.rejected, (state) => {
				state.checkoutLoading = false;
			})
			.addCase(fetchCart.rejected, (state) => {
				state.loading = false;
				state.error = true;
			})
			.addMatcher(isAnyOf(
				addCartItem.fulfilled,
				addCartItems.fulfilled
			), (state) => {
				state.removeCount = 0;
			})
			.addMatcher(
				isAnyOf(
					removeCartItem.fulfilled,
					removeCartItems.fulfilled,
					addCartItem.fulfilled,
					addCartItems.fulfilled,
					updateCartItem.fulfilled,
					fetchCart.fulfilled,
					addDiscountCode.fulfilled
				), (state, action) => {
					return {
						...state,
						...action.payload,
						loading: false,
						updating: false,
					};
				});
	},
});

export const {
	openCart,
	closeCart,
	setDeliveryFrequency,
	setMealsPerWeek,
	toggleCheckoutLoading,
	resetRemoveCount,
	selectBox,
	selectMinBox,
	fetchCartSuccess,
} = cartSlice.actions;

export default cartSlice.reducer;
